import React from "react";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import LocalShipping from "@mui/icons-material/LocalShipping";
import Delete from "@mui/icons-material/Delete";
import CloudUpload from "@mui/icons-material/CloudUpload";
import Visibility from "@mui/icons-material/Visibility";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import { ActionsButtons } from "components/CustomButtons/ActionsButtons";
import { RETENTION_OUTPUTS_LABELS } from "./labels";
import { formatNumber } from "helpers/getNumberFormats";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";
import { useDispatch } from "react-redux";
import { downloadReceipt } from "redux/actions/retentionOutputs/retentionOutputsActions";
import { getRetentionRowActions } from "./retentionRowActions";

const { headers, tooltips } = RETENTION_OUTPUTS_LABELS;

const RetentionOutputActions = ({ original, onAccept, onReject, onDeliver, onDelete, onUploadEvidence, onViewEvidence }) => {
  const dispatch = useDispatch();
  const { status, urlEvidence, id } = original;
  const isCreated = status === "created";

  const isAR = useRuleValidator(PERMISSIONS.WITHHOLDINGS_A_R);
  const isDelivery = useRuleValidator(PERMISSIONS.WITHHOLDINGS_DELIVERY);
  const isDel = useRuleValidator(PERMISSIONS.WITHHOLDINGS_DELETE);
  const canUploadEvidence = useRuleValidator(PERMISSIONS.WITHHOLDINGS_PDF);

  const actions = getRetentionRowActions({ status, urlEvidence }, { canUploadEvidence });

  const handleDownload = () => dispatch(downloadReceipt(id));

  const buttons = [];

  // 1. created → Aceptar, Rechazar, Eliminar
  if (isCreated) {
    buttons.push({ Icon: CheckCircle, color: 1, onClick: () => onAccept(original), tooltip: tooltips.accept, validation: isAR });
    buttons.push({ Icon: Cancel, color: 4, onClick: () => onReject(original), tooltip: tooltips.reject, validation: isAR });
    buttons.push({ Icon: Delete, color: 3, onClick: () => onDelete(original), tooltip: tooltips.delete, validation: isDel });
  }

  // 2. authorized / delivered (cualquier tipo, incluidos los cierres) → recibo siempre;
  //    carga de evidencia hasta que se sube, luego consulta; en authorized con evidencia se habilita la entrega
  if (actions.pdf) {
    buttons.push({
      Icon: PictureAsPdf,
      color: 0,
      onClick: handleDownload,
      tooltip: tooltips.pdf,
      validation: true,
    });
  }
  if (actions.upload) {
    buttons.push({ Icon: CloudUpload, color: 1, onClick: () => onUploadEvidence(original), tooltip: tooltips.uploadEvidence, validation: true });
  }
  if (actions.evidence) {
    buttons.push({ Icon: Visibility, color: 1, onClick: () => onViewEvidence(original), tooltip: tooltips.viewEvidence, validation: true });
  }
  if (actions.deliver) {
    buttons.push({ Icon: LocalShipping, color: 0, onClick: () => onDeliver(original), tooltip: tooltips.deliver, validation: isDelivery });
  }

  return <ActionsButtons buttons={buttons} />;
};

export const columns = ({ onAccept, onReject, onDeliver, onDelete, onUploadEvidence, onViewEvidence }) => [
  {
    Header: headers.customerName,
    accessor: "customerName",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.customerName || "").toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.advanceDate,
    accessor: "created",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.created || "").toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.userAR,
    accessor: "authorizedBy",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.authorizedBy || "").toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.userRequest,
    accessor: "createdBy",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.createdBy || "").toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.userDelivery,
    accessor: "deliveredBy",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.deliveredBy || "").toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.amount,
    accessor: "amount",
    filterable: true,
    Cell: ({ value }) => <div>$ {formatNumber(value)}</div>,
    filterMethod: (filter, row) =>
      String(row?.amount || "").includes(filter.value),
  },
  {
    Header: headers.type,
    accessor: "typeLabel",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.typeLabel || "").toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.status,
    accessor: "statusLabel",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row?.statusLabel || "").toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.description,
    accessor: "description",
    filterable: false,
  },
  {
    Header: headers.actions,
    filterable: false,
    Cell: ({ original }) => (
      <RetentionOutputActions
        original={original}
        onAccept={onAccept}
        onReject={onReject}
        onDeliver={onDeliver}
        onDelete={onDelete}
        onUploadEvidence={onUploadEvidence}
        onViewEvidence={onViewEvidence}
      />
    ),
  },
];
